import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Key } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import AddApiKeyModal from '../components/api-keys/AddApiKeyModal';
import EditApiKeyModal from '../components/api-keys/EditApiKeyModal';
import DeleteApiKeyModal from '../components/api-keys/DeleteApiKeyModal';
import ApiKeyCreatedModal from '../components/api-keys/ApiKeyCreatedModal';
import { formatDate } from '../utils/dateUtils';
import { useApiKeys } from '../hooks/useApiKeys';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const AbilitiesBadge = ({ abilities }) => {
    const { t } = useLanguage();
    if (!abilities || abilities.length === 0) return <span className="text-text-muted text-xs">—</span>;
    if (abilities.includes('*')) {
        return (
            <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-accent/10 text-accent">
                {t(KEYS.API_KEYS_FULL_ACCESS)}
            </span>
        );
    }
    return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-surface-overlay text-text-secondary border border-surface-border">
            {abilities.length === 1
                ? t(KEYS.API_KEYS_SCOPES_SINGULAR).replace('{n}', abilities.length)
                : t(KEYS.API_KEYS_SCOPES_PLURAL).replace('{n}', abilities.length)}
        </span>
    );
};

const ApiKeys = () => {
    const { data: apiKeys = [], isLoading: loading, error, refetch } = useApiKeys();
    const { t } = useLanguage();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editKey, setEditKey] = useState(null);
    const [deleteKey, setDeleteKey] = useState(null);
    const [createdToken, setCreatedToken] = useState(null);

    const handleCreated = ({ token, apiKey }) => {
        setCreatedToken({ token, apiKeyName: apiKey.name });
    };

    const handleRegenerated = ({ token, apiKey }) => {
        setCreatedToken({ token, apiKeyName: apiKey.name });
    };

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary">{t(KEYS.API_KEYS_TITLE)}</h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.API_KEYS_SUBTITLE)}
                    </p>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.API_KEYS_ADD)}
                </Button>
            </div>

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={3} columns={5} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.API_KEYS_ERROR_LOAD)} onRetry={refetch} />
            ) : apiKeys.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.API_KEYS_COL_NAME)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.API_KEYS_COL_ABILITIES)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">
                                        {t(KEYS.API_KEYS_COL_LAST_USED)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">
                                        {t(KEYS.API_KEYS_COL_EXPIRES)}
                                    </th>
                                    <th className="px-4 py-2.5 w-20"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {apiKeys.map((key, index) => (
                                    <tr
                                        key={key.id}
                                        className="hover:bg-surface-overlay transition-colors group animate-appear"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <td className="px-4 py-2.5 text-sm text-text-primary font-medium">
                                            {key.name}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <AbilitiesBadge abilities={key.abilities} />
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                            {key.last_used_at ? formatDate(key.last_used_at) : t(KEYS.COMMON_NEVER)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden sm:table-cell">
                                            {key.expires_at ? formatDate(key.expires_at) : t(KEYS.COMMON_NEVER)}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() => setEditKey(key)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                    title="Regenerate"
                                                >
                                                    <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteKey(key)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <Card className="animate-appear">
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Key className="w-8 h-8 text-text-muted mb-3" />
                        <p className="text-sm font-medium text-text-primary mb-1">{t(KEYS.API_KEYS_EMPTY_TITLE)}</p>
                        <p className="text-xs text-text-muted mb-4">
                            {t(KEYS.API_KEYS_EMPTY_DESCRIPTION)}
                        </p>
                        <Button variant="primary" size="small" onClick={() => setIsAddModalOpen(true)} className="gap-2">
                            <Plus className="w-3.5 h-3.5" />
                            {t(KEYS.API_KEYS_ADD)}
                        </Button>
                    </div>
                </Card>
            )}

            {/* Modals */}
            <AddApiKeyModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onCreated={handleCreated}
            />
            <EditApiKeyModal
                isOpen={!!editKey}
                onClose={() => setEditKey(null)}
                apiKey={editKey}
                onRegenerated={handleRegenerated}
            />
            <DeleteApiKeyModal
                isOpen={!!deleteKey}
                onClose={() => setDeleteKey(null)}
                apiKey={deleteKey}
            />
            <ApiKeyCreatedModal
                isOpen={!!createdToken}
                onClose={() => setCreatedToken(null)}
                token={createdToken?.token}
                apiKeyName={createdToken?.apiKeyName}
            />
        </div>
    );
};

export default ApiKeys;
